import {Component, EventEmitter, Input, OnDestroy, OnInit, OnChanges, SimpleChanges, Output, signal, ApplicationRef, EnvironmentInjector, ChangeDetectionStrategy} from '@angular/core';
import * as L from 'leaflet';
import 'leaflet-gesture-handling';
import {CanvasInitialView, CanvasPoint} from '@shared/models/structure';
import {Tile, TileId} from '@shared/models/tile';
import {PopoverSelectTilesComponent} from '@lib/components/popover-multi-select/object-types/tiles/popover-select-tiles.component';
import {Option, OptionGroup} from '@shared/models/option';
import {TilesService} from '@core/services/tiles.service';
import {PopoverMenuComponent} from '@lib/components/popover-menu/popover-menu.component';
import {CANVAS_MINI_TILE} from '@features/canvas-tiles/shared/canvas-tile.constants';
import {SpinnerComponent} from '@lib/components/spinner/spinner.component';
import {FileUploadService} from '@core/services/file-upload.service';
import {ObjectType} from '@shared/models/object.model';
import {ShadeToolsService} from '@core/services/shade-tools.service';
import {FaIconComponent} from '@lib/components/fa-icon/fa-icon.component';
import {CanvasSnapGrid} from '@features/canvas-tiles/shared/canvas-snap-grid';
import {CanvasPointMove} from '@features/canvas-tiles/shared/canvas-snap-grid.models';
import {addCanvasMapControls, CanvasClusterLayer, CANVAS_MAP_CONFIG, createCanvasMap, destroyCanvasMap, UNSET_INITIAL_VIEW} from '@features/canvas-tiles/shared/canvas-map-controls';
import {buildPointMenuOptions, isSamePoint, PointMenuOptionId, swapTileIds} from '@features/canvas-tiles/shared/canvas-point-utils';
import {CanvasMarkerRegistry} from '@features/canvas-tiles/shared/canvas-marker-registry';
import {PointContextMenuState, TileSelectorState} from '@features/canvas-tiles/editor/models/canvas-editor-ui.model';
import {MarkerHandle} from '@features/canvas-tiles/shared/canvas-marker.models';

@Component({
  selector: 'motn-canvas-editor',
  standalone: true,
  imports: [PopoverSelectTilesComponent, PopoverMenuComponent, SpinnerComponent, FaIconComponent],
  templateUrl: './canvas-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./canvas-editor.component.scss']
})
export class CanvasEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tileId: string;
  @Input() imageUrl = '';
  @Input() canvasPoints: CanvasPoint[] = [];
  @Input() clusterEnabled = true;
  @Input() initialView?: CanvasInitialView;
  @Output() canvasPointsChange = new EventEmitter<CanvasPoint[]>();
  @Output() imageChange = new EventEmitter<{uuid: string; url: string}>();
  @Output() clusterChange = new EventEmitter<boolean>();
  @Output() initialViewChange = new EventEmitter<CanvasInitialView>();

  private map?: L.Map;
  private clusterLayer?: CanvasClusterLayer;
  private snapGrid?: CanvasSnapGrid;
  private gridLayer?: L.LayerGroup;
  private snapEnabled = false;
  private suppressClicks = false;
  private releaseClicksTimer?: ReturnType<typeof setTimeout>;
  private imageLoadId = 0;
  private imageWidth = 0;
  private imageHeight = 0;
  private readonly markerRegistry: CanvasMarkerRegistry;
  private markerHandles: Map<string, MarkerHandle> = new Map();

  uploadingCanvasImage = false;
  loading = signal(true);
  hasDeletedTiles = signal(false);
  userTiles: Tile[] = [];

  tileSelector = signal<TileSelectorState | null>(null);
  contextMenu = signal<PointContextMenuState | null>(null);
  private ignoreNextMapClick = false;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
    private tilesService: TilesService,
    private fileUploadService: FileUploadService,
    private shadeTools: ShadeToolsService
  ) {
    this.markerRegistry = new CanvasMarkerRegistry(this.appRef, this.injector, () => this.clusterLayer);
  }

  ngOnInit(): void {
    this.tilesService.fetchUserTiles$().subscribe({
      next: tiles => {
        this.userTiles = tiles.filter(tile => tile.typeSupportsBeingInFolder());
        this.removeDeletedTiles();
      },
      error: () => {
        this.userTiles = [];
      },
    });
  }

  private removeDeletedTiles(): void {
    const validIds = new Set(this.userTiles.map(tile => tile.ID));
    const results = this.canvasPoints.map(original => {
      const TileIDs = original.TileIDs.filter(id => validIds.has(id));
      return {
        original,
        cleaned: TileIDs.length > 0 ? {...original, TileIDs} : null,
        changed: TileIDs.length < original.TileIDs.length,
      };
    });
    const changed = results.filter(result => result.changed);
    if (changed.length === 0) return;

    if (this.map) {
      const unmatched = [...changed];
      [...this.markerHandles.values()].forEach(handle => {
        const index = unmatched.findIndex(result => isSamePoint(result.original, handle.point));
        if (index === -1) return;

        const [{cleaned}] = unmatched.splice(index, 1);
        this.removeMarkerEntry(handle.markerId);
        if (cleaned) this.addMarkerToMap(cleaned);
      });
    }

    const newPoints = results.flatMap(result => result.cleaned ?? []);
    this.canvasPoints = newPoints;
    this.hasDeletedTiles.set(true);
    this.canvasPointsChange.emit(newPoints);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imageUrl'] && this.imageUrl) {
      this.loading.set(true);
      this.cleanupMap();
      this.initializeMap();
    }
  }

  ngOnDestroy(): void {
    this.imageLoadId++;
    this.cleanupMap();
  }

  private cleanupMap(): void {
    if (!this.map) return;

    const mapRef = this.map;
    this.map = undefined;

    this.markerRegistry.clear();
    this.markerHandles.clear();
    this.clusterLayer?.remove();
    this.clusterLayer = undefined;
    this.gridLayer = undefined;
    this.snapGrid = undefined;
    this.snapEnabled = false;
    this.suppressClicks = false;
    clearTimeout(this.releaseClicksTimer);
    destroyCanvasMap(mapRef);
  }

  private initializeMap(): void {
    const loadId = ++this.imageLoadId;
    const img = new Image();
    img.onload = () => {
      if (loadId !== this.imageLoadId) return;
      this.imageWidth = img.width;
      this.imageHeight = img.height;
      this.setupMap();
    };
    img.onerror = () => {
      if (loadId === this.imageLoadId) this.loading.set(false);
    };
    img.src = this.imageUrl;
  }

  private setupMap(): void {
    this.snapGrid = new CanvasSnapGrid(this.imageWidth, this.imageHeight);
    const {map, fitToImage} = createCanvasMap(
      'canvas-editor-map', this.imageUrl, {width: this.imageWidth, height: this.imageHeight},
      this.initialView, {padding: [0, 0]}, () => this.loading.set(false),
    );
    this.map = map;

    this.map.on('moveend', () => this.emitCurrentView());
    this.map.on('zoomend', () => this.emitCurrentView());

    this.clusterLayer = new CanvasClusterLayer(
      this.map, CANVAS_MINI_TILE.WIDTH, CANVAS_MAP_CONFIG.maxZoom,
      members => this.onClusterMembersClick(members)
    );
    this.clusterLayer.addTo(this.map);
    this.clusterLayer.setEnabled(this.clusterEnabled);

    addCanvasMapControls(this.map, {
      onFitBounds: () => {
        fitToImage();
        this.initialViewChange.emit(UNSET_INITIAL_VIEW);
      },
      clusterToggle: {
        enabled: this.clusterEnabled,
        onChange: (enabled) => {
          this.clusterEnabled = enabled;
          this.clusterLayer?.setEnabled(enabled);
          this.clusterChange.emit(enabled);
        },
      },
      snapToggle: {
        enabled: this.snapEnabled,
        onChange: (enabled) => {
          this.snapEnabled = enabled;
          this.setGridVisible(enabled);
          if (enabled) this.snapExistingPoints();
        },
      },
    });

    this.renderPoints();

    this.map.on('click', (e: L.LeafletMouseEvent) => this.onMapClick(e));
  }

  private setGridVisible(visible: boolean): void {
    if (!this.map || !this.snapGrid) return;
    if (!visible) {
      this.gridLayer?.remove();
      return;
    }
    this.gridLayer ??= this.snapGrid.createLayer();
    this.gridLayer.addTo(this.map);
  }

  private snapExistingPoints(): void {
    if (!this.snapGrid) return;
    const {moves, unsnapped} = this.snapGrid.snapPoints(this.canvasPoints);
    this.repositionPoints(moves);
    if (unsnapped > 0) {
      const plural = unsnapped !== 1;
      this.shadeTools.infoDialog(`${unsnapped} ${plural ? 'markers are' : 'marker is'} too crowded to snap and ${plural ? 'were' : 'was'} left where ${plural ? 'they are' : 'it is'}. Move ${plural ? 'them' : 'it'} by hand.`, 'Snap to grid');
    }
  }

  private repositionPoints(moves: CanvasPointMove[]): void {
    const handles = [...this.markerHandles.values()];
    const handleByPoint = new Map(handles.map(h => [h.point, h]));
    const claimed = new Set<MarkerHandle>();
    const entries: {handle: MarkerHandle; to: CanvasPoint}[] = [];
    moves.forEach(move => {
      const direct = handleByPoint.get(move.from);
      const handle = direct && !claimed.has(direct)
        ? direct
        : handles.find(h => !claimed.has(h) && isSamePoint(h.point, move.from));
      if (!handle) return;
      claimed.add(handle);
      entries.push({handle, to: move.to});
    });
    this.applyMoves(entries);
  }

  private applyMoves(entries: {handle: MarkerHandle; to: CanvasPoint}[]): void {
    if (entries.length === 0) return;

    const claimedIndexes = new Set<number>();
    const newPoints = [...this.canvasPoints];
    const indexByPoint = new Map(this.canvasPoints.map((p, i) => [p, i]));
    entries.forEach(({handle, to}) => {
      const direct = indexByPoint.get(handle.point);
      const index = direct !== undefined && !claimedIndexes.has(direct)
        ? direct
        : this.canvasPoints.findIndex((p, i) => !claimedIndexes.has(i) && isSamePoint(p, handle.point));
      if (index === -1) return;
      claimedIndexes.add(index);
      newPoints[index] = to;
      handle.point = to;
      this.markerRegistry.get(handle.markerId)?.marker.setLatLng(this.toLatLng(to));
    });

    this.canvasPoints = newPoints;
    this.clusterLayer?.setMarkers(this.markerRegistry.markers());
    this.canvasPointsChange.emit(newPoints);
  }

  private resolvePosition(latlng: L.LatLng): {X: number; Y: number} {
    return this.snapGrid!.resolvePosition(latlng, this.snapEnabled);
  }

  private onClusterMembersClick(members: L.Marker[]): void {
    if (!this.map || members.length === 0) return;
    const bounds = L.latLngBounds(members.map(m => m.getLatLng()));
    this.map.fitBounds(bounds, { padding: [80, 80], maxZoom: CANVAS_MAP_CONFIG.maxZoom });
  }

  private emitCurrentView(): void {
    if (!this.map) return;

    const center = this.map.getCenter();
    this.initialViewChange.emit({
      CenterX: center.lng / this.imageWidth,
      CenterY: center.lat / this.imageHeight,
      Zoom: this.map.getZoom(),
    });
  }

  private renderPoints(): void {
    this.clusterLayer?.setMarkers(this.canvasPoints.map(point => this.addPointMarker(point)));
  }

  private toLatLng(point: CanvasPoint): L.LatLngTuple {
    return [point.Y * this.imageHeight, point.X * this.imageWidth];
  }

  private addMarkerToMap(point: CanvasPoint): void {
    if (!this.clusterLayer) return;
    this.clusterLayer.addMarker(this.addPointMarker(point));
  }

  private addPointMarker(point: CanvasPoint): L.Marker {
    const markerId = this.markerRegistry.nextId();
    const handle: MarkerHandle = {markerId, point};
    this.markerHandles.set(markerId, handle);

    const {marker} = this.markerRegistry.add(markerId, {
      position: this.toLatLng(point),
      tileIds: point.TileIDs,
      shouldPoll: true,
      draggable: true,
      onClick: ({event, index}) => this.onMiniTileClick(handle.markerId, event, handle.point, index),
    });

    let dragging = false;
    marker.on('dragstart', () => {
      dragging = true;
      this.suppressClicks = true;
      this.ignoreNextMapClick = false;
      clearTimeout(this.releaseClicksTimer);
    });
    marker.on('dragend', () => {
      dragging = false;
      this.onMarkerDragEnd(handle, marker);
    });
    marker.on('remove', () => {
      if (!dragging) return;
      dragging = false;
      marker.setLatLng(this.toLatLng(handle.point));
      this.releaseClicksSoon();
    });
    return marker;
  }

  private releaseClicksSoon(): void {
    clearTimeout(this.releaseClicksTimer);
    this.releaseClicksTimer = setTimeout(() => this.suppressClicks = false, 100);
  }

  private onMarkerDragEnd(handle: MarkerHandle, marker: L.Marker): void {
    this.releaseClicksSoon();

    const point = handle.point;
    const moved: CanvasPoint = { ...point, ...this.resolvePosition(marker.getLatLng()) };

    if (moved.X === point.X && moved.Y === point.Y) {
      marker.setLatLng(this.toLatLng(point));
      return;
    }

    this.applyMoves([{handle, to: moved}]);
  }

  private onMapClick(e: L.LeafletMouseEvent): void {
    if (this.suppressClicks) return;

    if (this.ignoreNextMapClick) {
      this.ignoreNextMapClick = false;
      return;
    }
    if (this.tileSelector()) return;

    this.tileSelector.set({
      screenPosition: {x: e.originalEvent.clientX, y: e.originalEvent.clientY},
      canvasPosition: this.resolvePosition(e.latlng),
      selectedTileIds: [],
    });
  }

  onTileSelected(option: Option): void {
    const selectedTileIds = this.tileSelector()?.selectedTileIds;
    if (!selectedTileIds) return;

    const tileId = option.id as TileId;
    const index = selectedTileIds.indexOf(tileId);
    if (index === -1) {
      selectedTileIds.push(tileId);
    } else {
      selectedTileIds.splice(index, 1);
    }
  }

  onClickOutsideTileSelector(): void {
    this.ignoreNextMapClick = true;
    const selector = this.tileSelector();
    if (selector && selector.selectedTileIds.length > 0) {
      this.addPoint({TileIDs: selector.selectedTileIds, ...selector.canvasPosition});
    }
    this.closeSelector();
  }

  private closeSelector(): void {
    this.tileSelector.set(null);
  }

  private addPoint(point: CanvasPoint): void {
    const newPoints = [...this.canvasPoints, point];
    this.canvasPoints = newPoints;

    this.addMarkerToMap(point);
    this.canvasPointsChange.emit(newPoints);
  }

  private removePoint(markerId: string): void {
    const handle = this.markerHandles.get(markerId);
    if (!handle) return;

    const index = this.canvasPoints.findIndex(p => isSamePoint(p, handle.point));
    if (index === -1) return;

    const newPoints = this.canvasPoints.filter((_, i) => i !== index);
    this.canvasPoints = newPoints;

    this.removeMarkerEntry(markerId);
    this.canvasPointsChange.emit(newPoints);
  }

  private onMiniTileClick(markerId: string, event: MouseEvent, point: CanvasPoint, tileIndex?: number): void {
    event.stopPropagation();
    if (this.suppressClicks) return;

    this.closeSelector();
    this.contextMenu.set({
      screenPosition: {x: event.clientX, y: event.clientY},
      markerId,
      point,
      tileIndex: tileIndex ?? null,
      options: new OptionGroup(buildPointMenuOptions(point, tileIndex ?? null)),
    });
  }

  onContextMenuOptionClicked(option: Option): void {
    switch (option.id) {
      case PointMenuOptionId.DELETE:
        return this.deleteContextMenuPoint();
      case PointMenuOptionId.ADD_REMOVE:
        return this.editContextMenuPointTiles();
      case PointMenuOptionId.MOVE_UP:
        return this.moveTile(-1);
      case PointMenuOptionId.MOVE_DOWN:
        return this.moveTile(1);
    }
  }

  closeContextMenu(): void {
    this.contextMenu.set(null);
  }

  private deleteContextMenuPoint(): void {
    const menu = this.contextMenu();
    if (menu) this.removePoint(menu.markerId);
    this.closeContextMenu();
  }

  private editContextMenuPointTiles(): void {
    const menu = this.contextMenu();
    if (!menu) return;

    this.removePoint(menu.markerId);
    this.closeContextMenu();
    this.tileSelector.set({
      screenPosition: menu.screenPosition,
      canvasPosition: {X: menu.point.X, Y: menu.point.Y},
      selectedTileIds: menu.point.TileIDs.filter(id => this.userTiles.some(tile => tile.ID === id)),
    });
  }

  private moveTile(delta: -1 | 1): void {
    const menu = this.contextMenu();
    const swapped = menu && menu.tileIndex !== null ? swapTileIds(menu.point.TileIDs, menu.tileIndex, delta) : null;
    if (menu && swapped) {
      this.removePoint(menu.markerId);
      this.addPoint({...menu.point, TileIDs: swapped});
    }
    this.closeContextMenu();
  }

  private removeMarkerEntry(markerId: string): void {
    const marker = this.markerRegistry.remove(markerId);
    if (marker) {
      this.clusterLayer?.removeMarker(marker);
    }
    this.markerHandles.delete(markerId);
  }

  onCanvasImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    if (!file.type.startsWith('image/')) {
      this.shadeTools.errorDialog('Please select an image file.');
      return;
    }

    this.uploadingCanvasImage = true;

    const objectId = this.tileId;
    const objectType = ObjectType.TILE;

    this.fileUploadService.create(file, objectId, objectType).subscribe({
      next: (upload) => {
        this.imageChange.emit({
          uuid: upload.UUID,
          url: upload.URL
        });
        this.uploadingCanvasImage = false;
        input.value = '';
      },
      error: () => {
        this.uploadingCanvasImage = false;
        this.shadeTools.errorDialog('Failed to upload image. Please try again.');
        input.value = '';
      }
    });
  }
}
